import io from "socket.io-client";
import api from "../services/api";

const RECONNECT_DELAY = 1000;

// Quantas vezes esperar o token aparecer antes de desistir. Na tela de login
// ele nunca vem, então não pode ser uma espera infinita.
const MAX_TOKEN_WAIT = 60;

// O backend (libs/socket.ts) derruba o handshake que chega sem token, e o
// token só entra em api.defaults depois que o /auth/refresh_token responde.
// Enquanto isso o localStorage já tem o token da sessão anterior, que o
// backend aceita mesmo expirado.
const readToken = () => {
  const fromApi = api.defaults.headers.Authorization;
  if (fromApi) return fromApi;

  try {
    const stored = localStorage.getItem("token");
    return stored ? `Bearer ${JSON.parse(stored)}` : null;
  } catch (err) {
    return null;
  }
};

class SocketWorker {
  constructor(companyId, userId) {
    const current = SocketWorker.instance;

    // Logout + login de outro usuário na mesma aba: o socket antigo aponta
    // para o namespace da empresa anterior e nunca mais autentica.
    const changedIdentity =
      current &&
      companyId &&
      userId &&
      (String(current.companyId) !== String(companyId) ||
        String(current.userId) !== String(userId));

    if (changedIdentity) {
      current.destroy();
    }

    if (!SocketWorker.instance) {
      this.companyId = companyId;
      this.userId = userId;
      this.token = null;
      this.socket = null;
      this.eventListeners = {}; // Armazena os ouvintes de eventos registrados
      this.reconnectTimer = null;
      this.tokenWaitAttempts = 0;
      this.configureSocket();
      SocketWorker.instance = this;
    }

    return SocketWorker.instance;
  }

  // O socket é criado uma única vez e nunca substituído: os ouvintes que os
  // componentes registram ficam presos a este objeto, e trocá-lo os perderia
  // em silêncio. Sobe com autoConnect false para nunca conectar sem token.
  configureSocket() {
    this.socket = io(
      `${process.env.REACT_APP_BACKEND_URL}/${this?.companyId}`,
      {
        autoConnect: false,
        reconnection: true,
        reconnectionDelay: RECONNECT_DELAY,
        reconnectionAttempts: Infinity,
        query: { userId: this.userId }
      }
    );

    this.socket.on("connect", () => {
      this.tokenWaitAttempts = 0;
    });

    // Quando é o servidor que encerra a conexão (token ausente ou de outra
    // empresa), o socket.io-client não reconecta sozinho: temos que reabrir.
    //
    // Nos demais motivos ele reconecta por conta própria, e reagendar aqui
    // criava um segundo laço de reconexão correndo junto com o dele: sessões
    // abertas e descartadas em sequência (três em três segundos no log) e
    // respostas 400 "Session ID unknown" do servidor.
    this.socket.on("disconnect", reason => {
      if (reason === "io server disconnect") {
        this.scheduleReconnect();
      }
    });

    this.connect();
  }

  // Adiciona um ouvinte de eventos
  on(event, callback) {
    if (!this.socket) return;

    this.tokenWaitAttempts = 0;
    this.connect();
    this.socket.on(event, callback);

    // Armazena o ouvinte no objeto de ouvintes
    if (!this.eventListeners[event]) {
      this.eventListeners[event] = [];
    }
    this.eventListeners[event].push(callback);
  }

  // Emite um evento
  emit(event, data) {
    if (!this.socket) return;

    this.connect();
    this.socket.emit(event, data);
  }

  // Desconecta um ou mais ouvintes de eventos
  off(event, callback) {
    if (!this.socket || !this.eventListeners[event]) return;

    if (callback) {
      // Desconecta um ouvinte específico
      this.socket.off(event, callback);
      this.eventListeners[event] = this.eventListeners[event].filter(
        cb => cb !== callback
      );
    } else {
      // Desconecta todos os ouvintes do evento
      this.eventListeners[event].forEach(cb => this.socket.off(event, cb));
      delete this.eventListeners[event];
    }
  }

  disconnect() {
    this.clearReconnect();

    if (this.socket) {
      this.socket.disconnect();
    }
  }

  // Garante que o socket esteja conectado e com o token atual na query.
  connect() {
    if (!this.socket) {
      this.configureSocket();
      return;
    }

    if (this.socket.connected) return;

    const token = readToken();

    if (!token) {
      if (this.tokenWaitAttempts >= MAX_TOKEN_WAIT) {
        console.warn("Socket.IO: sem token de autenticação, conexão não iniciada");
        return;
      }

      this.tokenWaitAttempts += 1;
      this.scheduleReconnect();
      return;
    }

    if (token !== this.token) {
      this.token = token;
      this.socket.io.opts.query = { userId: this.userId, token };
    }

    this.socket.connect();
  }

  scheduleReconnect() {
    if (this.reconnectTimer) return;

    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      this.connect();
    }, RECONNECT_DELAY);
  }

  clearReconnect() {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
  }

  isConnected() {
    return Boolean(this.socket && this.socket.connected);
  }

  forceReconnect() {
    this.clearReconnect();
    this.tokenWaitAttempts = 0;
    this.connect();
  }

  // Só quando a identidade muda: o socket antigo e seus ouvintes são inúteis.
  destroy() {
    this.clearReconnect();

    if (this.socket) {
      this.socket.removeAllListeners();
      this.socket.disconnect();
      this.socket = null;
    }

    this.eventListeners = {};

    if (SocketWorker.instance === this) {
      SocketWorker.instance = null;
    }
  }
}

const instance = (companyId, userId) => new SocketWorker(companyId, userId);

export default instance;
